
"use client";
import useWishList from "@/hooks/useWishList";
import React, { useMemo, useCallback } from "react";
import { useDictionary } from "../DictProvider/DictProvider";

interface WishListLoaderProps {
  id: string;
  isCategory?: boolean;
  isProduct?: boolean;
  isCart?: boolean;
}

const WishListLoader: React.FC<WishListLoaderProps> = ({
  id,
  isCategory = false,
  isProduct = false,
  isCart = false,
}) => {
  const { loading, wishlist, addtWishList, removetWishList } = useWishList();
  const transalate = useDictionary();

  // Memoize the wishlist existence check to avoid recalculation
  const isWishListExist = useMemo(
    () => wishlist?.some((item) => item.product._id === id),
    [wishlist, id]
  );

  // Memoize the click handlers
  const handleAddToWishList = useCallback(
    () => addtWishList(id),
    [addtWishList, id]
  );
  const handleRemoveFromWishList = useCallback(
    () => removetWishList(id),
    [removetWishList, id]
  );

  // Memoize the dynamic classes to prevent recalculating them on each render
  const containerClass = useMemo(() => {
    if (isProduct) return "";
    if (isCategory) return "absolute top-0 ltr:right-0 rtl:left-0";
    return "absolute xl:right-12 lg:right-12 md:right-12 right-4 top-0";
  }, [isCategory, isProduct]);

  return (
    <>
      {isWishListExist ? (
        <div
          className={`cursor-pointer flex gap-x-1 items-center ${containerClass}`}
          onClick={handleRemoveFromWishList}
        >
          {loading[id] ? (
            <svg
              className="animate-spin h-[24px] w-[24px] text-gray-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.963 7.963 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          ) : (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M22.5 9.5625C22.5 16.125 12.7697 21.4369 12.3553 21.6562C12.2461 21.715 12.124 21.7458 12 21.7458C11.876 21.7458 11.7539 21.715 11.6447 21.6562C11.2303 21.4369 1.5 16.125 1.5 9.5625C1.50174 8.02146 2.11468 6.54404 3.20436 5.45436C4.29404 4.36468 5.77146 3.75174 7.3125 3.75C9.24844 3.75 10.9434 4.5825 12 5.98969C13.0566 4.5825 14.7516 3.75 16.6875 3.75C18.2285 3.75174 19.706 4.36468 20.7956 5.45436C21.8853 6.54404 22.4983 8.02146 22.5 9.5625Z"
                fill="#AC7529"
              />
            </svg>
          )}
          {isCart && (
            <button className="text-sm xs:text-base font-semibold md:font-normal">
              {transalate?.cart?.wishlist}
            </button>
          )}
        </div>
      ) : (
        <div
          className={`cursor-pointer flex gap-x-1 items-center ${containerClass}`}
          onClick={handleAddToWishList}
        >
          {loading[id] ? (
            <svg
              className="animate-spin h-[24px] w-[24px] text-gray-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.963 7.963 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          ) : (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g opacity="0.42">
                <path
                  d="M12.0122 21.0051L12.0122 21.0051L12.0078 21.0075L12.0034 21.0051C11.8216 20.9089 9.34184 19.5575 6.91889 17.4355C4.4565 15.2791 2.25802 12.5048 2.25781 9.57464C2.25944 8.23237 2.79337 6.94554 3.7425 5.99641C4.69164 5.04727 5.97847 4.51334 7.32074 4.51172C9.03732 4.51184 10.503 5.24631 11.4081 6.45173L12.0078 7.25052L12.6076 6.45173C13.5126 5.24631 14.9783 4.51184 16.6949 4.51172C18.0372 4.51334 19.324 5.04727 20.2731 5.99641C21.2224 6.94564 21.7563 8.23265 21.7578 9.57506C21.7574 12.505 19.559 15.2792 17.0967 17.4355C14.6738 19.5575 12.194 20.9089 12.0122 21.0051Z"
                  stroke="black"
                  strokeWidth="1.5"
                />
              </g>
            </svg>
          )}
          {isCart && (
            <button className="text-sm xs:text-base font-semibold md:font-normal pl-[2px]">
              {transalate?.cart?.wishlist}
            </button>
          )}
        </div>
      )}
    </>
  );
};

export default WishListLoader;
